import { useBlocker } from '@tanstack/react-router';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { LeaveAlertDialog } from './LeaveAlertDialog';
import type { LeaveAlertOption } from './types';

/**
 * SidePanel用の離脱確認アラートコンポーネント
 *
 * - サイドパネルが閉じる前に未保存変更をチェック
 * - モード変更時にも未保存変更をチェック
 * - ルート遷移時にも未保存変更をチェック（useBlocker）
 * - ブラウザのリロード/タブクローズ時にも確認
 *
 * @example
 * ```tsx
 * // MainRouteLayout.tsxに配置
 * <SidePanelLeaveAlert />
 * ```
 */
export const SidePanelLeaveAlert: FC = () => {
  const { t } = useTranslation();
  const {
    hasUnsavedChanges,
    isRightOpen,
    mode,
    pendingAction,
    setHasUnsavedChanges,
    confirmPendingAction,
    cancelPendingAction,
    setUnsavedChangesGuard,
    setOpen,
  } = useRouteSidePanelStore(
    useShallow((s) => ({
      hasUnsavedChanges: s.hasUnsavedChanges,
      isRightOpen: s.isOpen,
      mode: s.mode,
      pendingAction: s.pendingAction,
      setHasUnsavedChanges: s.setHasUnsavedChanges,
      confirmPendingAction: s.confirmPendingAction,
      cancelPendingAction: s.cancelPendingAction,
      setUnsavedChangesGuard: s.setUnsavedChangesGuard,
      setOpen: s.setOpen,
    })),
  );

  // modeがai-chatでcontextがcreateFromPdfの場合、assessment-comparison、revenue-edit、またはexpenditure-editの場合にアラートを有効にする
  const context = mode?.type === 'ai-chat' ? mode.context : undefined;
  const isCreateFromPdfMode =
    (context?.route === 'projectCreate' || context?.route === 'projectEdit') &&
    context?.mode === 'createFromPdf';
  const isAssessmentComparisonMode = mode?.type === 'assessment-comparison';
  const isRevenueEditMode = mode?.type === 'settlement-statistics-converter.revenue-edit';
  const isExpenditureEditMode = mode?.type === 'settlement-statistics-converter.expenditure-edit';
  const isMasterCreateMode = mode?.type === 'settlement-statistics-converter.master';
  const isRevenueAppropriationMode =
    mode?.type === 'settlement-statistics-converter.revenue-appropriation';
  const isAccountingTypesMode = mode?.type === 'master-management.accounting-types';
  const isExpenditureItemsMode = mode?.type === 'master-management.expenditure-items';
  const isOrganizationsMode = mode?.type === 'master-management.organizations';
  const shouldShowAlert =
    hasUnsavedChanges &&
    isRightOpen &&
    (isCreateFromPdfMode ||
      isAssessmentComparisonMode ||
      isRevenueEditMode ||
      isExpenditureEditMode ||
      isMasterCreateMode ||
      isRevenueAppropriationMode ||
      isAccountingTypesMode ||
      isExpenditureItemsMode ||
      isOrganizationsMode);

  // ルート遷移ブロック用の状態
  const [isRouteBlocked, setIsRouteBlocked] = useState(false);

  // ルート遷移をブロック（createFromPdfモードでダーティな場合のみ）
  const blocker = useBlocker({
    shouldBlockFn: () => shouldShowAlert,
    enableBeforeUnload: false, // beforeunloadは別途処理
    disabled: !shouldShowAlert,
    withResolver: true,
  });

  // 未保存変更ガード関数を登録（shouldShowAlertがtrueの場合はfalseを返してブロック）
  useEffect(() => {
    setUnsavedChangesGuard(() => !shouldShowAlert);
    return () => setUnsavedChangesGuard(null);
  }, [shouldShowAlert, setUnsavedChangesGuard]);

  // beforeunload対応（ブラウザのリロード/タブクローズ時）
  useEffect(() => {
    if (!shouldShowAlert) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldShowAlert]);

  // useBlockerの状態を監視
  useEffect(() => {
    if (blocker.status === 'blocked') {
      setIsRouteBlocked(true);
    } else if (blocker.status === 'idle') {
      setIsRouteBlocked(false);
    }
  }, [blocker.status]);

  // ダイアログを表示するかどうか
  const isDialogOpen = pendingAction !== null || isRouteBlocked;

  const handleConfirm = () => {
    setHasUnsavedChanges(false);

    // ルート遷移がブロックされている場合
    if (isRouteBlocked && blocker.proceed) {
      blocker.proceed();
      setIsRouteBlocked(false);
      // ルート遷移確認時はサイドパネルも閉じる（遷移後にデータが不整合になるため）
      setOpen({ isOpen: false, mode: undefined });
    }

    // ストアのpendingActionがある場合
    if (pendingAction !== null) {
      confirmPendingAction();
    }
  };

  const handleCancel = () => {
    // ルート遷移がブロックされている場合
    if (isRouteBlocked && blocker.reset) {
      blocker.reset();
      setIsRouteBlocked(false);
    }

    // ストアのpendingActionがある場合
    if (pendingAction !== null) {
      cancelPendingAction();
    }
  };

  const alertOptions: LeaveAlertOption = (() => {
    if (isCreateFromPdfMode) {
      return {
        title: t('side-panel-leave-alert.title'),
        description: t('side-panel-leave-alert.description'),
        cancelLabel: t('common.actions.cancel'),
        confirmLabel: t('side-panel-leave-alert.confirm'),
      };
    }
    return {
      title: t('panel-leave-alert.title'),
      description: t('panel-leave-alert.description'),
      cancelLabel: t('common.actions.cancel'),
      confirmLabel: t('side-panel-leave-alert.confirm'),
    };
  })();

  return (
    <LeaveAlertDialog
      isOpen={isDialogOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      options={alertOptions}
    />
  );
};
